import { Client } from "@stomp/stompjs";
import apiService from "../Simplify360Chat/Services/APIService";
import { s360ConfigService } from "../Simplify360Chat/Services/S360Config";
import endpoints from "../utils/Constants/endpoints";
import {
  ConvoType,
  MessageType,
} from "../Simplify360Chat/Conversation/MessageBoxTypes/Message";

const activeClients = new Map();
const manualReconnectRequests = new Set();
const AUTO_RECONNECT_EVENT = "chatWSReconnected";

export const initChatWSConnection = (chatWorkitemId, resume) => {
  const effectiveChatWorkitemId =
    chatWorkitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`
    );

  const existingClient = activeClients.get(effectiveChatWorkitemId);
  if (existingClient && existingClient.connected) {
    // console.log(
    //   "WebSocket already connected for workitem:",
    //   effectiveChatWorkitemId
    // );
    return existingClient;
  }

  if (existingClient) {
    activeClients.delete(effectiveChatWorkitemId);
  }

  let timerKeepAlive;
  let keepAlive403Count = 0;
  let keepAliveAborted = false;
  let messageTypingTimeout = null;
  // Handlers are kept as refs to properly remove them and avoid leaks
  let visibilityChangeHandler = null;
  let pageHideHandler = null;
  let appOpenChangedHandler = null;
  let isAppOpenState = false; // only start when UI marks open
  const MIN_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 30000;
  let reconnectAttempts = 0;

  const computeReconnectDelay = (attempt = 0) => {
    if (attempt <= 0) {
      return MIN_RECONNECT_DELAY;
    }
    const exponentialDelay =
      MIN_RECONNECT_DELAY * Math.pow(2, Math.max(attempt - 1, 0));
    return Math.min(exponentialDelay, MAX_RECONNECT_DELAY);
  };

  const startKeepAlive = () => {
    if (
      timerKeepAlive ||
      document.visibilityState !== "visible" ||
      !isAppOpenState ||
      keepAliveAborted
    )
      return;

    if (!keepAliveAborted) {
      apiService
        .keepAlive(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId
        )
        .then(() => {
          // reset streak on success
          keepAlive403Count = 0;
        });
    }
    timerKeepAlive = setInterval(() => {
      if (keepAliveAborted) return;
      apiService
        .keepAlive(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId
        )
        .then(() => {
          // reset streak on success
          keepAlive403Count = 0;
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 403) {
            apiService.refreshAuthToken();
            keepAlive403Count += 1;
            if (keepAlive403Count >= 2) {
              // Abort further keepAlive attempts
              keepAliveAborted = true;
              stopKeepAlive();
            }
          } else {
            // reset on non-403 errors
            keepAlive403Count = 0;
          }
        });
    }, 10000);
  };

  const stopKeepAlive = () => {
    if (timerKeepAlive) {
      clearInterval(timerKeepAlive);
      timerKeepAlive = null;
    }
  };

  const attachVisibilityHandlers = () => {
    if (visibilityChangeHandler || pageHideHandler) return;
    visibilityChangeHandler = () => {
      if (document.visibilityState === "hidden") {
        stopKeepAlive();
      } else {
        if (isAppOpenState && !keepAliveAborted) {
          startKeepAlive();
        }
      }
    };
    pageHideHandler = () => {
      stopKeepAlive();
    };
    document.addEventListener("visibilitychange", visibilityChangeHandler);
    window.addEventListener("pagehide", pageHideHandler);
  };

  const detachVisibilityHandlers = () => {
    if (visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", visibilityChangeHandler);
      visibilityChangeHandler = null;
    }
    if (pageHideHandler) {
      window.removeEventListener("pagehide", pageHideHandler);
      pageHideHandler = null;
    }
  };

  const attachAppOpenHandler = () => {
    if (appOpenChangedHandler) return;
    appOpenChangedHandler = (e) => {
      const next = !!e?.detail?.isAppOpen;
      isAppOpenState = next;
      if (!isAppOpenState) {
        stopKeepAlive();
      } else {
        if (!keepAliveAborted) {
          startKeepAlive();
        }
      }
    };
    window.addEventListener("S360AppOpenChanged", appOpenChangedHandler);
  };

  // Add new event listener for stopKeepAlive
  let stopKeepAliveHandler = null;

  const attachStopKeepAliveHandler = () => {
    if (stopKeepAliveHandler) return;
    const workitemId =
      effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId;
    stopKeepAliveHandler = (e) => {
      keepAliveAborted = true;
      // Call stopKeepAlive when event is received
      stopKeepAlive();
      detachAppOpenHandler();
      detachVisibilityHandlers();
      detachStopKeepAliveHandler();

      // Clean up the client for this workitem
      if (client && client.connected) {
        client.deactivate();
      }
      activeClients.delete(workitemId);
    };
    window.addEventListener(
      `stopKeepAlive_${workitemId}`,
      stopKeepAliveHandler
    );
  };

  const detachStopKeepAliveHandler = () => {
    if (stopKeepAliveHandler) {
      const workitemId =
        effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId;
      window.removeEventListener(
        `stopKeepAlive_${workitemId}`,
        stopKeepAliveHandler
      );
      stopKeepAliveHandler = null;
    }
  };

  const detachAppOpenHandler = () => {
    if (appOpenChangedHandler) {
      window.removeEventListener("S360AppOpenChanged", appOpenChangedHandler);
      appOpenChangedHandler = null;
    }
  };

  const client = new Client({
    brokerURL: endpoints.WS_URL.replace(
      ":location",
      s360ConfigService.config.location.split("https://")[1]
    ),
    connectionTimeout: 10000,
    reconnectDelay: computeReconnectDelay(),
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    debug: (msg) => console.debug(`[STOMP][${effectiveChatWorkitemId}] ${msg}`),
    beforeConnect: () => {
      if (keepAliveAborted) {
        return;
      }
      if (reconnectAttempts > 0) {
        client.reconnectDelay = computeReconnectDelay(reconnectAttempts);
      }
    },
    onConnect: () => {
      const hadManualRequest = manualReconnectRequests.has(
        effectiveChatWorkitemId
      );
      const autoReconnect = reconnectAttempts > 0 && !hadManualRequest;

      if (hadManualRequest) {
        manualReconnectRequests.delete(effectiveChatWorkitemId);
      }

      if (autoReconnect) {
        window.dispatchEvent(
          new CustomEvent(AUTO_RECONNECT_EVENT, {
            detail: {
              workitemId: effectiveChatWorkitemId,
              attempts: reconnectAttempts,
              timestamp: Date.now(),
            },
          })
        );
      }

      reconnectAttempts = 0;
      client.reconnectDelay = computeReconnectDelay();
      // Store the client for this workitem
      activeClients.set(effectiveChatWorkitemId, client);

      if (!s360ConfigService.config.preview) {
        if (s360ConfigService.config.isNewConversation) {
          // Attach handlers and kick off keep-alive only when tab is visible
          attachVisibilityHandlers();
          attachAppOpenHandler();
          attachStopKeepAliveHandler();
          // Sync initial state if UI updated it before we attached
          try {
            if (typeof window !== "undefined") {
              isAppOpenState = !!window.__S360AppOpenState;
            }
          } catch (_) {}
          startKeepAlive();
        }
        if (!resume) return;

        apiService.triggerChatRequest(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId
        );
      }

      client.subscribe(
        endpoints.WS_CHAT_SUB.replace(
          ":chatWorkitemId",
          effectiveChatWorkitemId
        ).replace(":tenant", s360ConfigService.config.tenantId),
        (message) => {
          const body = JSON.parse(message.body);

          switch (body.name) {
            case "WorkitemAssignUserNotification":
              window.dispatchEvent(
                new CustomEvent("agentConnected", {
                  detail: { workitemId: effectiveChatWorkitemId },
                })
              );
              break;
            case "WorkitemTerminatedNotification":
              {
                keepAliveAborted = true;
                stopKeepAlive();
                detachVisibilityHandlers();
                detachAppOpenHandler();
                detachStopKeepAliveHandler();
                client.unsubscribe();

                // Clean up this specific workitem's client
                activeClients.delete(effectiveChatWorkitemId);

                const messageEvent = new CustomEvent(body.name, {
                  detail: body,
                });
                window.dispatchEvent(messageEvent);
              }
              break;
            case "MessageNotification":
              //HANDLING SOCKET EVENT ON PREVIEW - STEP 1
              const messageObject = body.engagementData;

              if (messageObject?.fromUser) {
                if (s360ConfigService) {
                  if (s360ConfigService && s360ConfigService.config) {
                    s360ConfigService.config.nickName = body.message?.fromId;
                  }
                }

                if (
                  ![null, undefined].includes(body) &&
                  ![null, undefined].includes(body.message) &&
                  ![null, undefined].includes(body.message.fromId)
                ) {
                  messageObject.fromUser.messageFromId = body.message.fromId;
                } else {
                  messageObject.fromUser.messageFromId = "";
                }
              }
              const messageEvent = new CustomEvent(body.name, {
                detail: messageObject,
              });
              window.dispatchEvent(messageEvent);
              break;
            case "EngagementDataStatusUpdateNotification":
              const messageStatusEvent = new CustomEvent(body.name, {
                detail: body,
              });
              window.dispatchEvent(messageStatusEvent);
              break;
            case "MessageTypingNotification":
              const messagTypingeEvent = new CustomEvent(body.name, {
                detail: body,
              });
              clearTimeout(messageTypingTimeout);
              window.dispatchEvent(messagTypingeEvent);
              messageTypingTimeout = setTimeout(() => {
                const messagTypingeEvent = new CustomEvent(body.name, {
                  detail: null,
                });
                window.dispatchEvent(messagTypingeEvent);
              }, 2000);
              break;
            case "MessageStatusNotification": // code handling pending
              return;
            case "WebSocketSubscriptionStatusNotification":
              if (
                s360ConfigService.config.preview &&
                !s360ConfigService.config.previewInitialized
              ) {
                apiService
                  .initPreview()
                  .then((res) => {
                    s360ConfigService.config.previewInitialized = true;
                  })
                  .catch((err) => {});
              }
          }
        }
      );
    },
    onDisconnect: () => {
      sessionStorage?.removeItem("subscribed");
      stopKeepAlive();
      detachVisibilityHandlers();
      detachAppOpenHandler();
      detachStopKeepAliveHandler();
      if (messageTypingTimeout) {
        clearTimeout(messageTypingTimeout);
        messageTypingTimeout = null;
      }

      // Clean up this workitem's client on disconnect
      activeClients.delete(effectiveChatWorkitemId);
      manualReconnectRequests.delete(effectiveChatWorkitemId);
    },
    onWebSocketClose: (event) => {
      if (keepAliveAborted) {
        return;
      }
      reconnectAttempts += 1;
      const delay = computeReconnectDelay(reconnectAttempts);
      client.reconnectDelay = delay;
      console.warn(
        `WebSocket closed for workitem ${effectiveChatWorkitemId}. ` +
          `Retrying in ${delay}ms (attempt ${reconnectAttempts}).`,
        event?.reason
      );
    },
    onWebSocketError: (event) => {
      console.error(
        `WebSocket error for workitem ${effectiveChatWorkitemId}.`,
        event
      );
    },
  });

  client.activate();
  return client;
};

// Utility function to check if a workitem has an active connection
export const hasActiveConnection = (workitemId) => {
  const client = activeClients.get(workitemId);
  return client && client.connected;
};

// Utility function to disconnect a specific workitem
export const disconnectWorkitem = (workitemId) => {
  const client = activeClients.get(workitemId);
  if (client && client.connected) {
    client.deactivate();
  }
  activeClients.delete(workitemId);
  manualReconnectRequests.delete(workitemId);
};

// Safe reconnection utility - prevents multiple connections and preserves existing ones
export const safeReconnectWebSocket = (chatWorkitemId, resume = true) => {
  const effectiveChatWorkitemId =
    chatWorkitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`
    );

  if (!effectiveChatWorkitemId) {
    console.warn("Cannot reconnect WebSocket: No workitem ID available");
    return null;
  }

  manualReconnectRequests.add(effectiveChatWorkitemId);

  // CRITICAL: Check if already connected - NEVER create duplicate connections
  const existingClient = activeClients.get(effectiveChatWorkitemId);
  if (existingClient && existingClient.connected) {
    console.log(
      "WebSocket already connected and healthy for workitem:",
      effectiveChatWorkitemId
    );
    manualReconnectRequests.delete(effectiveChatWorkitemId);
    return existingClient; // Return existing connection - DO NOT create new one
  }

  // Check if client exists but is disconnected
  if (existingClient && !existingClient.connected) {
    console.log(
      "Found disconnected WebSocket client, cleaning up before reconnection:",
      effectiveChatWorkitemId
    );
    // Clean up disconnected client
    try {
      existingClient.deactivate();
    } catch (error) {
      console.warn("Error deactivating disconnected client:", error);
    }
    activeClients.delete(effectiveChatWorkitemId);
  }

  console.log(
    "Creating new WebSocket connection for workitem:",
    effectiveChatWorkitemId
  );
  const newClient = initChatWSConnection(chatWorkitemId, resume);

  if (!newClient) {
    manualReconnectRequests.delete(effectiveChatWorkitemId);
  }

  return newClient;
};

// Enhanced connection status checker with detailed logging
export const getConnectionStatus = (workitemId) => {
  const effectiveWorkitemId =
    workitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`
    );

  const client = activeClients.get(effectiveWorkitemId);

  const status = {
    workitemId: effectiveWorkitemId,
    hasClient: !!client,
    isConnected: client && client.connected,
    clientState: client
      ? client.connected
        ? "connected"
        : "disconnected"
      : "none",
    timestamp: new Date().toISOString(),
  };

  console.log("WebSocket connection status:", status);
  return status;
};
